<template>
  <wl-person
    @click.native="handleCtaLink"
    :name="name"
    :header-span-text="age"
    :location="location"
    :name-link="nameLink"
    :has-itemscope="hasItemscope"
    :show-age-label="false"
    name-rel="noreferrer noopener nofollow"
    @handle-name-link="handleCtaLink"
    :has-name-link-handler="true"
  >
    <template #header>
      <div
        class="person__button-hold person__button-hold--header"
        v-for="item in renderIf(ctaLabel)"
        :key="item"
      >
        <a
          :href="nameLink"
          v-text="ctaLabel"
          class="person__button"
          @click.prevent="handleCtaLink"
        />
      </div>
    </template>
    <p class="person__blurb" v-html="cardBlurb(person)" />
    <component
      v-for="({ list, label, itempropType, hasItemscope, separator, className },
      index) in lists"
      v-bind:is="`wl-person-list${hasAddress(list) ? '-address' : ''}`"
      :itemprop-type="itempropType"
      :list="list"
      :label="label"
      :class="className"
      :separator="separator"
      :has-itemscope="hasItemscope"
      :key="index + 'list'"
    />
    <div class="person__button-hold person__button-hold--content">
      <button role="button" class="person__button" v-text="ctaLabel" />
    </div>
  </wl-person>
</template>

<script>
import WlPerson from '@/components/shared/wl-person';
import WlPersonList from '@/components/shared/wl-person-list';
import WlPersonListAddress from '@/components/shared/wl-person-list-address';
import resultHelper from '@/assets/js/resultHelper';
import formatPhoneNumber from '@/assets/js/helpers/formatPhoneNumber';
const NBars = require('nbars/commonjs.js');
const pageCms = require(`@/assets/cms/pages/results.json`);

export default {
  name: 'ic-person',
  components: { WlPersonListAddress, WlPerson, WlPersonList },
  data() {
    const person = this.person || {};
    const { city, state, age } = person;
    const { query } = this.$route;
    // Toggle to control the CSS-only visual blur of phone/email values.
    const disableBlur = true;

    return {
      age: age >= 80 ? ', age 80+' : age >= 18 ? `, age ${age}` : '',
      disableBlur,
      lists: resultHelper.getPersonLists(person, disableBlur),
      name: resultHelper.formatFullName(person),
      nameLink: resultHelper.getEverFlowLink(person, query),
      location: resultHelper.formatCity(city) + ', ' + state,
      ctaLabel: pageCms.person_card.cta_text,
      ctaLink: resultHelper.getEverFlowLink(person, query),
      hasItemscope: true,
      isClicked: false
    };
  },
  methods: {
    handleCtaLink(evt) {
      const targetIsRelative =
        evt.target.getAttribute('class') === 'person__link';
      if (targetIsRelative || !this.ctaLink || this.isClicked) {
        return false;
      }

      evt.preventDefault();

      // Fix: Prevent Safari Double Click on Anchor Links
      this.isClicked = true;
      setTimeout(() => {
        this.isClicked = false;
      }, 500);

      // Opens new link
      window.open(this.ctaLink, '_self');
    },
    /**
     * Does the list have an address?
     * @param list
     * @returns {boolean}
     */
    hasAddress(list) {
      return (
        Array.isArray(list) && typeof list[0] === 'object' && list[0].state
      );
    },
    /**
     * Render If
     * @param condition
     * @returns {[]}
     */
    renderIf(condition) {
      return resultHelper.renderIf(condition);
    },
    /**
     * Social media profile text
     * @param person
     * @returns {string}
     */
    getSocialMediaProfileText(person) {
      const count = Array.isArray(person.social_media)
        ? person.social_media.length
        : 0;

      if (count === 0) {
        return null;
      }

      const profilePlural =
        count === 1
          ? pageCms.person_card.social_media.profiles_singular
          : pageCms.person_card.social_media.profiles_plural;

      return NBars.transform(pageCms.person_card.social_media.tooltip_text, {
        socialCount: count,
        profilePlural: profilePlural
      });
    },
    /**
     * Format an email from its structured parts, leaving the first 2 letters
     * of the user portion visible and wrapping the rest of the user in a span
     * so it can be visually blurred via CSS while remaining present in the
     * DOM/source. Everything from the @ on is shown unmasked.
     *
     * @param {object} email
     * @param {string} email.user
     * @param {string} email.domain
     * @param {string} email.affix
     * @param {string} [email.full]
     * @param {boolean} [disableBlur=false] When true, the user portion is not
     *   wrapped in a `person__blur` span, so the email renders unmasked.
     * @returns {string}
     */
    formatEmailWithBlur(email, disableBlur = false) {
      if (!email) {
        return '';
      }
      const user = String(email.user ?? '').toLowerCase();
      const domain = String(email.domain ?? '').toLowerCase();
      const affix = String(email.affix ?? '').toLowerCase();
      if (!user || !domain || !affix) {
        return String(email.full ?? '').toLowerCase();
      }
      const tail = `@${domain}.${affix}`;
      const visible = user.slice(0, 2);
      const blurred = user.slice(2);
      if (disableBlur || !blurred) {
        return `${user}${tail}`;
      }
      return `${visible}<span class="person__blur">${blurred}</span>${tail}`;
    },
    /**
     * Format a phone number from its structured parts, wrapping the line
     * (last 4 digits) in a span so it can be visually blurred via CSS while
     * remaining present in the DOM/source.
     *
     * @param {object} phone
     * @param {string} phone.area
     * @param {string} phone.office
     * @param {string} phone.line
     * @param {string} [phone.full]
     * @param {boolean} [disableBlur=false] When true, the line (last 4 digits)
     *   is not wrapped in a `person__blur` span, so the phone renders unmasked.
     * @returns {string}
     */
    formatPhoneNumberWithBlur(phone, disableBlur = false) {
      if (!phone) {
        return '';
      }
      const { area, office, line } = phone;
      if (!area || !office || !line) {
        return formatPhoneNumber({ phoneNumber: phone.full });
      }
      if (disableBlur) {
        return `(${area}) ${office}-${line}`;
      }
      return `(${area}) ${office}-<span class="person__blur">${line}</span>`;
    },
    /**
     * Check if card number is included in the pattern array. Is used to display a specific blurb for each card
     *
     * @param startIndex
     * @param cardNumber
     * @returns {boolean}
     */
    isBlurbMultipleOf(startIndex, cardNumber) {
      let arr = [];
      const numberOfItemsPerPage = 25;
      const numberToAppend = 4;
      for (let i = startIndex; i <= numberOfItemsPerPage; i += numberToAppend) {
        arr.push(i);
      }
      return arr.includes(cardNumber);
    },
    cardBlurb(person) {
      const {
        phones,
        relatives,
        dob,
        addresses,
        firstname,
        lastname,
        city,
        emails
      } = person;
      const firstName = resultHelper.formatName(firstname);
      const lastName = resultHelper.formatName(lastname);
      const age = resultHelper.getAge(dob);
      const birthday = new Date(dob).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }); // formats date to MM/DD/YYYY
      const phoneNumber =
        phones && phones.length > 0
          ? `<strong>${this.formatPhoneNumberWithBlur(
              phones[0],
              this.disableBlur
            )}</strong>`
          : null;
      let phoneNumbers = '';
      let previousPhoneNumbers = null;
      if (phones && phones.length >= 2) {
        phoneNumbers = phones
          .map((phone, index) => {
            if (index === 0) {
              return `<strong>${this.formatPhoneNumberWithBlur(
                phone,
                this.disableBlur
              )}</strong>`;
            }
            return this.formatPhoneNumberWithBlur(phone, this.disableBlur);
          })
          .slice(0, 2)
          .join(' and ');
        previousPhoneNumbers = phones
          .map(phone => this.formatPhoneNumberWithBlur(phone, this.disableBlur))
          .slice(1, 3)
          .join(' and ');
      } else if (phones && phones.length === 1) {
        phoneNumbers = `<strong>${this.formatPhoneNumberWithBlur(
          phones[0],
          this.disableBlur
        )}</strong>`;
      } else {
        phoneNumbers = '';
      }

      const filterCurrentAddress = addresses
        .filter(address => address.current_address)
        .filter(
          currentAddress =>
            currentAddress.street_name &&
            currentAddress.street_number !== 0 &&
            currentAddress.city &&
            currentAddress.state &&
            currentAddress.zip
        );
      const filterNonCurrentAddress = addresses
        .filter(address => !address.current_address)
        .filter(
          currentAddress =>
            currentAddress.street_name &&
            currentAddress.street_number !== 0 &&
            currentAddress.city &&
            currentAddress.state &&
            currentAddress.zip
        );

      let currentAddress;
      if (filterCurrentAddress.length > 0) {
        currentAddress = resultHelper.formatAddress(
          filterCurrentAddress[0],
          '',
          false
        );
      } else if (filterNonCurrentAddress.length > 0) {
        currentAddress = resultHelper.formatAddress(
          filterNonCurrentAddress[0],
          '',
          false
        );
      } else {
        currentAddress = '';
      }
      // remove duplicate cities
      const removeDuplicateCities =
        addresses.length > 0
          ? addresses
              .slice(1)
              .filter(
                (address, index, array) =>
                  array.findIndex(
                    addressTwo =>
                      addressTwo.city === address.city || address.city === city
                  ) === index
              )
          : '';

      let previousCitiesWithStates = '';
      if (removeDuplicateCities && removeDuplicateCities.length > 2) {
        previousCitiesWithStates = resultHelper
          .previousCities(removeDuplicateCities.slice(0, 2), true)
          .join(' and ');
      } else if (removeDuplicateCities && removeDuplicateCities.length === 1) {
        previousCitiesWithStates = resultHelper
          .previousCities(removeDuplicateCities, true)
          .join();
      }

      let previousCities = '';
      if (removeDuplicateCities && removeDuplicateCities.length > 2) {
        previousCities = resultHelper
          .previousCities(removeDuplicateCities.slice(0, 2))
          .join(' and ');
      } else if (removeDuplicateCities && removeDuplicateCities.length === 1) {
        previousCities = resultHelper
          .previousCities(removeDuplicateCities)
          .join();
      }

      const formatRelativesArray = relatives
        ? relatives.map(relative => {
            const firstName = resultHelper.formatName(relative.firstname);
            const lastName = resultHelper.formatName(relative.lastname);
            return `${firstName} ${lastName}`;
          })
        : '';
      // Remove duplicate relative names
      const relativesList = [...new Set(formatRelativesArray)];

      let defaultRelatives = '';
      let relativesWithCount = '';
      if (relativesList && relativesList.length >= 2) {
        defaultRelatives =
          relativesList.slice(0, 2).join(', ') + ' and others. ';
        relativesWithCount = ` ${relativesList.slice(0, 2).join(', ')} and ${
          relativesList.slice(2).length > 0 ? relativesList.slice(2).length : ''
        } others. `;
      } else if (relativesList && relativesList.length === 1) {
        relativesWithCount = defaultRelatives = relativesList[0] + '. ';
      }

      let multipleRelatives = '';
      if (relativesList && relativesList.length > 3) {
        multipleRelatives = relativesList
          .slice(0, 3)
          .join(', ')
          .replace(/,(?!.*,)/gim, ' and');
      } else if (relativesList && relativesList.length >= 2) {
        multipleRelatives = relativesList
          .slice(0, 2)
          .join(', ')
          .replace(/,(?!.*,)/gim, ' and');
      } else if (relativesList && relativesList.length === 1) {
        multipleRelatives = relativesList[0];
      }
      const latestEmailAddress = emails?.[0]
        ? this.formatEmailWithBlur(emails[0], this.disableBlur)
        : null;

      const cardBlurbs = pageCms.card_blurbs;
      let blurbNumber;
      const blurbOne = this.isBlurbMultipleOf(1, this.cardNumber);
      const blurbTwo = this.isBlurbMultipleOf(2, this.cardNumber);
      const blurbThree = this.isBlurbMultipleOf(3, this.cardNumber);
      const blurbFour = this.isBlurbMultipleOf(4, this.cardNumber);

      // Set the specific blurb for each card
      if (blurbOne) {
        blurbNumber = 0;
      } else if (blurbTwo) {
        blurbNumber = 1;
      } else if (blurbThree) {
        blurbNumber = 2;
      } else if (blurbFour) {
        blurbNumber = 3;
      }

      let ageSentence = '';
      if (
        cardBlurbs[blurbNumber] &&
        cardBlurbs[blurbNumber].age_sentence &&
        age !== null
      ) {
        ageSentence = NBars.transform(cardBlurbs[blurbNumber].age_sentence, {
          firstName,
          lastName,
          age,
          dob: birthday,
          birthYear: birthday.substring(birthday.lastIndexOf('/') + 1)
        });
      }

      let currentAddressSentence = '';
      if (cardBlurbs[blurbNumber].address_sentence && currentAddress) {
        currentAddressSentence = NBars.transform(
          cardBlurbs[blurbNumber].address_sentence,
          {
            firstName: firstName,
            lastName: lastName,
            currentAddress: currentAddress
          }
        );
      }

      let previousCitiesSentence = '';
      if (
        cardBlurbs[blurbNumber] &&
        cardBlurbs[blurbNumber].previous_cities_sentence &&
        (previousCities || previousCitiesWithStates)
      ) {
        previousCitiesSentence = NBars.transform(
          cardBlurbs[blurbNumber].previous_cities_sentence,
          {
            firstName,
            lastName,
            previousCitiesWithStates: previousCitiesWithStates,
            previousCities: previousCities
          }
        );
      }

      let phoneNumberSentence = '';
      if (
        cardBlurbs[blurbNumber] &&
        cardBlurbs[blurbNumber].phone_number_sentence &&
        (phoneNumber || phoneNumbers)
      ) {
        phoneNumberSentence = NBars.transform(
          cardBlurbs[blurbNumber].phone_number_sentence,
          {
            firstName,
            phoneNumber,
            phoneNumbers
          }
        );
      }

      let previousPhoneNumberSentence = '';
      if (
        cardBlurbs[blurbNumber] &&
        cardBlurbs[blurbNumber].previous_phone_number_sentence &&
        previousPhoneNumbers
      ) {
        previousPhoneNumberSentence = NBars.transform(
          cardBlurbs[blurbNumber].previous_phone_number_sentence,
          {
            firstName,
            phoneNumbers: previousPhoneNumbers
          }
        );
      }

      let relativesSentence = '';
      if (
        cardBlurbs[blurbNumber] &&
        cardBlurbs[blurbNumber].relatives_sentence &&
        (defaultRelatives || multipleRelatives)
      ) {
        relativesSentence = NBars.transform(
          cardBlurbs[blurbNumber].relatives_sentence,
          {
            firstName,
            lastName,
            relatives: defaultRelatives,
            relativesWithCount: relativesWithCount,
            multipleRelatives
          }
        );
      }

      let emailAddressSentence = '';
      if (
        cardBlurbs[blurbNumber] &&
        cardBlurbs[blurbNumber].email_address_sentence &&
        latestEmailAddress
      ) {
        emailAddressSentence = NBars.transform(
          cardBlurbs[blurbNumber].email_address_sentence,
          {
            firstName,
            lastName,
            currentEmail: latestEmailAddress
          }
        );
      }

      let fullSentence = '';
      if (cardBlurbs[blurbNumber] && cardBlurbs[blurbNumber].blurb) {
        fullSentence = NBars.transform(cardBlurbs[blurbNumber].blurb, {
          ageSentence,
          currentAddressSentence,
          previousCitiesSentence,
          relativesSentence,
          phoneNumberSentence,
          previousPhoneNumberSentence,
          emailAddressSentence
        });
      }

      return fullSentence;
    }
  },
  props: {
    /*
      An object of a singular person that comes from the api response. Data would be used to populate the wl-person
      component and sub components
     */
    person: {
      type: Object,
      required: true,
      default: {}
    },
    /* Numbers each card. Starting with 1 */
    cardNumber: {
      type: Number,
      required: true
    }
  }
};
</script>
